import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useTrackEvent from "@/hooks/useTrackEvent";
import { LoadingSpinner, CustomizableSelect, IconClose } from "@velaro/velaro-component-lib";
import useAIConfigs from "@/hooks/useAIConfigs";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useSubscriptions from "@/hooks/useSubscriptions";
import useApi from "@/hooks/useApi";
import { AIConfiguration } from "../types";
import AINewSettingsTabs from "./AINewSettingsTabs";
import PromptEditor from "./PromptEditor";

interface ModelOption {
  provider: string;
  label: string;
  techName: string;
}

const FALLBACK_MODELS: ModelOption[] = [
  { provider: "Anthropic", label: "Claude Haiku 4.5 — Standard", techName: "claude-haiku-4-5-20251001" },
  { provider: "Anthropic", label: "Claude Sonnet 4.6 — Advanced", techName: "claude-sonnet-4-6" },
  { provider: "OpenAI",    label: "GPT-4o Mini — Standard",       techName: "velaro-gpt-4o-mini" },
  { provider: "OpenAI",    label: "GPT-4o — Advanced",            techName: "velaro-gpt-4o" },
  { provider: "Google",    label: "Gemini 2.0 Flash — Standard",  techName: "gemini-2.0-flash" },
  { provider: "Google",    label: "Gemini 1.5 Pro — Advanced",    techName: "gemini-1.5-pro" },
];

export default function AINewSettingsGeneral() {
  const { id } = useParams();
  const { aiConfigurations, loading, update } = useAIConfigs();
  const { track } = useTrackEvent();
  const { azureIndexes } = useAzureIndexes();
  const { subscription } = useSubscriptions();
  const api = useApi();
  const [config, setConfig] = useState<AIConfiguration>();
  const [configIndex, setConfigIndex] = useState<number>();
  const [selectedIndexName, setSelectedIndexName] = useState<string>("");
  const [models, setModels] = useState<ModelOption[]>(FALLBACK_MODELS);

  useEffect(() => {
    api.messaging.get("AIConfiguration/available-models")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setModels(data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    if (aiConfigurations.length > 0 && id) {
      const index = aiConfigurations.findIndex((c) => c.id === parseInt(id));
      if (index !== -1) {
        setConfig(aiConfigurations[index]);
        setConfigIndex(index);
        setSelectedIndexName(aiConfigurations[index].indexName);
      }
    }
  }, [aiConfigurations, id]);

  const handleUpdate = (field: keyof AIConfiguration, value: any) => {
    if (!config || configIndex === undefined) return;
    const updated = { ...config, [field]: value };
    setConfig(updated);
    update(updated, configIndex);
    track("setting_change", "AIBot/GeneralSettingsSaved");
  };

  const handleAzureIndex = (indexName: string) => {
    if (!config || configIndex === undefined) return;
    // When an index is selected: auto-enable vector search (was "None" = bot ignores index).
    // When the index is cleared: reset vectorSearchMethod back to "None".
    const updated = {
      ...config,
      indexName,
      vectorSearchMethod: indexName
        ? (config.vectorSearchMethod === "None" || !config.vectorSearchMethod ? "File" : config.vectorSearchMethod)
        : "None",
    };
    setConfig(updated);
    update(updated, configIndex);
    setSelectedIndexName(indexName);
  };


  const languages = [
    { label: "Auto Detect" },
    { label: "English" },
    { label: "Spanish" }
  ];

  const replyLengths = [
    { label: "Short", sublabel: "(25 Words)" },
    { label: "Medium", sublabel: "(50 Words)" },
    { label: "Long", sublabel: "(100 Words)" }
  ];

  if (loading || !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        {/* Header */}
        <div className="border-b pb-4 mb-4">
          <div className="font-semibold text-lg">Chatbot Configuration</div>
          <div className="text-slate-500 text-sm">
            Configure your chatbot's behavior, instructions, and knowledge.
          </div>
        </div>

        {/* Internal Chatbot Name */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Internal Chatbot Name</div>
          <div className="text-slate-500 text-sm pb-2">
            A unique identifier for organizing and managing your chatbots within the app. Not visible to your customers or visitors.
          </div>
          <input
            type="text"
            value={config.name}
            onChange={(e) => handleUpdate("name", e.target.value)}
            placeholder="Enter a name"
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* Instructions — structured prompt editor */}
        <PromptEditor
          value={config.prompt}
          onChange={(prompt) => handleUpdate("prompt", prompt)}
          configName={config.name}
        />

        {/* AI Model */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">AI Model</div>
          <div className="text-slate-500 text-sm pb-2">
            The AI model will be used to generate answers for your chatbot.
          </div>
          {subscription?.enableCustomAiModel ? (
            <div className="text-sm">
              <CustomizableSelect
                options={models.map((m) => ({
                  label: m.provider,
                  sublabel: m.label,
                  action: () => handleUpdate("aiModel", m.techName)
                }))}
                defaultOption={(() => {
                  const model = models.find((m) => m.techName === config.aiModel) || models[0];
                  return {
                    label: model.provider,
                    sublabel: model.label,
                    action: () => handleUpdate("aiModel", model.techName)
                  };
                })()}
              />
            </div>
          ) : (
            <div className="text-sm text-slate-gray-500">
              {config.aiModel || "Platform Default (Claude Haiku 4.5)"}
              <span className="ml-2 text-xs text-slate-gray-400">— upgrade your plan or contact us to enable custom model selection</span>
            </div>
          )}
        </div>

        {/* Language & Reply Length */}
        <div className="py-3">
          <div className="flex">
            <div className="flex flex-col w-1/2 pr-3">
              <div className="font-semibold text-sm pb-1">Language</div>
              <div className="text-sm">
                <CustomizableSelect
                  options={languages.map((l) => ({
                    label: l.label,
                    action: () => handleUpdate("language", l.label)
                  }))}
                  defaultOption={(() => {
                    const lang = languages.find((l) => l.label === config.language) || languages[0];
                    return {
                      label: lang.label,
                      action: () => handleUpdate("language", lang.label)
                    };
                  })()}
                />
              </div>
            </div>
            <div className="flex flex-col w-1/2">
              <div className="font-semibold text-sm pb-1">Reply Length</div>
              <div className="text-sm">
                <CustomizableSelect
                  options={replyLengths.map((r) => ({
                    label: r.label,
                    sublabel: r.sublabel,
                    action: () => handleUpdate("replyLength", r.label)
                  }))}
                  defaultOption={(() => {
                    const replyLength = replyLengths.find((r) => r.label === config.replyLength) || replyLengths[0];
                    return {
                      label: replyLength.label,
                      sublabel: replyLength.sublabel,
                      action: () => handleUpdate("replyLength", replyLength.label)
                    };
                  })()}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Response Creativity */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Response Creativity</div>
          <div className="text-slate-500 text-sm pb-2">
            Controls how consistent vs. creative your bot's responses are.
          </div>
          <div className="flex gap-2">
            {[
              { label: "Precise", value: 0.2, desc: "Consistent, predictable answers — best for FAQs and support" },
              { label: "Balanced", value: 0.5, desc: "Reliable but natural — recommended for most bots" },
              { label: "Creative", value: 0.9, desc: "More varied responses — best for conversational or sales bots" },
            ].map((opt) => {
              const current = config.temperature ?? 0.5;
              const isSelected = Math.abs(current - opt.value) < 0.05;
              return (
                <button
                  key={opt.label}
                  type="button"
                  title={opt.desc}
                  onClick={() => handleUpdate("temperature", opt.value)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                    isSelected
                      ? "border-cornflower-blue-400 bg-cornflower-blue-50 text-cornflower-blue-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-cornflower-blue-200 hover:bg-slate-50"
                  }`}
                >
                  <div>{opt.label}</div>
                  <div className="text-xs font-normal text-slate-400 mt-0.5 leading-snug">{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Index - Hidden for now
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Index</div>
          <div className="text-slate-500 text-sm pb-2">
            A Unique Azure Index required for using File Vector Search. Consult Velaro Customer Support to have this modified.
          </div>
          <input
            type="text"
            value={config.indexName}
            onChange={(e) => handleUpdate("indexName", e.target.value)}
            placeholder=""
            className="w-full p-2 border rounded-md text-sm text-slate-500"
          />
        </div>
        */}

        {/* Training Index */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Knowledge Index</div>
          <div className="text-slate-500 text-sm pb-2">
            Connect a knowledge index so this bot can answer questions from your training data. To create an index: go to <strong>Chatbot Training</strong>, run a scraper or upload files, then click <strong>Publish to Index</strong>. The new index will appear in this dropdown.
          </div>
          <div className="flex w-1/2 pr-3">
            <div className="h-[40px] text-sm flex flex-1">
              <CustomizableSelect
                key={selectedIndexName}
                options={azureIndexes.map((index) => ({
                  label: index.name,
                  action: () => handleAzureIndex(index.name)
                }))}
                defaultOption={{
                  label: selectedIndexName || "Select an index",
                  action: () => {}
                }}
              />
              {selectedIndexName && (
                <button className="pl-2" onClick={() => handleAzureIndex("")}>
                  <IconClose className="text-slate-gray-500" />
                </button>
              )}
            </div>
          </div>
          {selectedIndexName && config.vectorSearchMethod !== "None" && (
            <p className="text-xs text-green-600 mt-1">✓ Index connected — bot will search this knowledge base</p>
          )}
          {selectedIndexName && (config.vectorSearchMethod === "None" || !config.vectorSearchMethod) && (
            <p className="text-xs text-amber-600 mt-1">⚠ Index selected but vector search is off — save this page to activate</p>
          )}
          {!selectedIndexName && (
            <p className="text-xs text-slate-400 mt-1">No index connected — bot will answer from its instructions only</p>
          )}
        </div>
      </div>
    </div>
  );
}
